// src/components/admin/DonorManagementSystem.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const DonorManagementSystem = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [donors, setDonors] = useState([]);
  const [donations, setDonations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const [donorProfile, setDonorProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;
  if (!API_URL) {
    throw new Error('API URL not configured');
  }

  // Helper function to get fresh token
  const getToken = () => localStorage.getItem('adminToken');

  // Fetch dashboard stats
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        toast.error('Please log in to access donor management');
        return;
      }
      const response = await fetch(`${API_URL}/donor-management/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
      } else {
        toast.error(data.message || 'Failed to load dashboard');
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Fetch donors
  const fetchDonors = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus) params.append('status', filterStatus);

      const response = await fetch(`${API_URL}/donor-management/donors?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDonors(data.data);
      } else {
        toast.error(data.message || 'Failed to load donors');
      }
    } catch (error) {
      toast.error('Failed to load donors');
    } finally {
      setLoading(false);
    }
  };

  // Fetch donations
  const fetchDonations = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDonations(data.data);
      } else {
        toast.error(data.message || 'Failed to load donations');
      }
    } catch (error) {
      toast.error('Failed to load donations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch campaigns
  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.data);
      } else {
        toast.error(data.message || 'Failed to load campaigns');
      }
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Fetch donor profile
  const fetchDonorProfile = async (id) => {
    try {
      setLoading(true);
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donors/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setDonorProfile(data.data);
      } else {
        toast.error(data.message || 'Failed to load donor profile');
      }
    } catch (error) {
      toast.error('Failed to load donor profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle add/edit donor
  const handleSaveDonor = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const method = formData.id ? 'PUT' : 'POST';
      const url = formData.id
        ? `${API_URL}/donor-management/donors/${formData.id}`
        : `${API_URL}/donor-management/donors`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success(formData.id ? 'Donor updated successfully' : 'Donor created successfully');
        setShowModal(false);
        setFormData({});
        fetchDonors();
      } else {
        toast.error(data.message || 'Failed to save donor');
      }
    } catch (error) {
      toast.error('Failed to save donor');
    }
  };

  // Handle delete donor
  const handleDeleteDonor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this donor?')) return;

    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donors/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Donor deleted successfully');
        fetchDonors();
      } else {
        toast.error(data.message || 'Failed to delete donor');
      }
    } catch (error) {
      toast.error('Failed to delete donor');
    }
  };

  // Handle add donation
  const handleSaveDonation = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Donation recorded successfully');
        setShowModal(false);
        setFormData({});
        fetchDonations();
        fetchDashboard();
      } else {
        toast.error(data.message || 'Failed to record donation');
      }
    } catch (error) {
      toast.error('Failed to record donation');
    }
  };

  // Handle save campaign
  const handleSaveCampaign = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const method = formData.id ? 'PUT' : 'POST';
      const url = formData.id
        ? `${API_URL}/donor-management/campaigns/${formData.id}`
        : `${API_URL}/donor-management/campaigns`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success(formData.id ? 'Campaign updated' : 'Campaign created');
        setShowModal(false);
        setFormData({});
        fetchCampaigns();
      } else {
        toast.error(data.message || 'Failed to save campaign');
      }
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  // Export donations to CSV
  const exportDonationsCSV = async () => {
    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donations/export/csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Donations exported successfully');
    } catch (error) {
      toast.error('Failed to export donations');
    }
  };

  // Add note to donor
  const handleAddNote = async (note, sendEmail = false) => {
    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donors/${selectedDonor}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note, note_type: 'general', send_email: sendEmail })
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        if (data.email_sent) {
          toast.success('Note added and email sent successfully!');
        } else if (sendEmail && !data.email_sent) {
          toast.warning(`Note added but email failed: ${data.email_error || 'Unknown error'}`);
        } else {
          toast.success('Note added successfully');
        }
        fetchDonorProfile(selectedDonor);
      } else {
        toast.error(data.message || 'Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const token = getToken();
      if (!token) return;
      const response = await fetch(`${API_URL}/donor-management/donors/${selectedDonor}/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Note deleted successfully');
        fetchDonorProfile(selectedDonor);
      } else {
        toast.error(data.message || 'Failed to delete note');
      }
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    if (activeTab === 'donors') fetchDonors();
    if (activeTab === 'donations') fetchDonations();
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab, searchTerm, filterStatus]);

  useEffect(() => {
    if (selectedDonor) {
      fetchDonorProfile(selectedDonor);
    }
  }, [selectedDonor]);

  // Dashboard View
  const renderDashboard = () => (
    <div className="dashboard-container">
      {/* Stats Cards */}
      <div className="row g-4 mb-4">
        <div className="col-md-6 col-lg-3">
          <div className="stat-card stat-card-1">
            <div className="stat-icon">
              <i className="bi bi-calendar-month"></i>
            </div>
            <div className="stat-content">
              <h6 className="stat-label">This Month</h6>
              <h3 className="stat-value">${dashboardData?.thisMonth?.toFixed(2) || '0.00'}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="stat-card stat-card-2">
            <div className="stat-icon">
              <i className="bi bi-calendar-range"></i>
            </div>
            <div className="stat-content">
              <h6 className="stat-label">This Year</h6>
              <h3 className="stat-value">${dashboardData?.thisYear?.toFixed(2) || '0.00'}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="stat-card stat-card-3">
            <div className="stat-icon">
              <i className="bi bi-person-plus"></i>
            </div>
            <div className="stat-content">
              <h6 className="stat-label">New Donors</h6>
              <h3 className="stat-value">{dashboardData?.newDonors || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-3">
          <div className="stat-card stat-card-4">
            <div className="stat-icon">
              <i className="bi bi-arrow-repeat"></i>
            </div>
            <div className="stat-content">
              <h6 className="stat-label">Returning Donors</h6>
              <h3 className="stat-value">{dashboardData?.returningDonors || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Top Lists */}
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="data-card">
            <div className="data-card-header">
              <i className="bi bi-trophy-fill me-2"></i>
              <h5 className="mb-0">Top 5 Donors</h5>
            </div>
            <div className="data-card-body">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Total Donated</th>
                    <th>Donations</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.topDonors?.map((donor, index) => (
                    <tr key={donor.id}>
                      <td>
                        <span className="rank-badge">{index + 1}</span>
                        {donor.name}
                      </td>
                      <td className="fw-bold text-success">${parseFloat(donor.total_donated).toFixed(2)}</td>
                      <td><span className="badge bg-secondary">{donor.donation_count}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="data-card">
            <div className="data-card-header">
              <i className="bi bi-megaphone-fill me-2"></i>
              <h5 className="mb-0">Top 5 Campaigns</h5>
            </div>
            <div className="data-card-body">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Raised</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.topCampaigns?.map((campaign, index) => (
                    <tr key={campaign.id}>
                      <td>
                        <span className="rank-badge">{index + 1}</span>
                        {campaign.title}
                      </td>
                      <td className="fw-bold text-primary">${parseFloat(campaign.raised_amount).toFixed(2)}</td>
                      <td>
                        <div className="progress" style={{ height: '24px' }}>
                          <div
                            className="progress-bar bg-success"
                            style={{ width: `${campaign.progress}%` }}
                          >
                            {campaign.progress}%
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .dashboard-container {
          animation: fadeInUp 0.6s ease-out;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 1.75rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-left: 4px solid #6B8E23;
          display: flex;
          align-items: center;
          gap: 1.25rem;
        }

        .stat-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(107, 142, 35, 0.2);
        }

        .stat-card-1 { border-left-color: #6B8E23; }
        .stat-card-2 { border-left-color: #2C5234; }
        .stat-card-3 { border-left-color: #4CAF50; }
        .stat-card-4 { border-left-color: #FF9800; }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
          background: linear-gradient(135deg, #6B8E23 0%, #58761C 100%);
        }

        .stat-card-2 .stat-icon { background: linear-gradient(135deg, #2C5234 0%, #1a3020 100%); }
        .stat-card-3 .stat-icon { background: linear-gradient(135deg, #4CAF50 0%, #388E3C 100%); }
        .stat-card-4 .stat-icon { background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%); }

        .stat-content {
          flex: 1;
        }

        .stat-label {
          color: #6c757d;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #212529;
          margin: 0;
        }

        .data-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .data-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
        }

        .data-card-header {
          background: linear-gradient(135deg, #6B8E23 0%, #58761C 100%);
          padding: 1.25rem 1.5rem;
          color: white;
          display: flex;
          align-items: center;
        }

        .data-card-header h5 {
          font-weight: 600;
        }

        .data-card-body {
          padding: 1.5rem;
        }

        .rank-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: #6B8E23;
          color: white;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          margin-right: 0.75rem;
        }

        .table thead th {
          border-bottom: 2px solid #6B8E23;
          color: #495057;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.8rem;
          letter-spacing: 0.5px;
        }

        .table tbody tr {
          transition: all 0.2s ease;
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
          transform: scale(1.01);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );

  // Donors List View
  const renderDonorsList = () => (
    <div>
      <div className="row mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search donors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <select
            className="form-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="col-md-5 text-end">
          <button
            className="btn btn-success"
            onClick={() => {
              setFormData({});
              setModalType('donor');
              setShowModal(true);
            }}
            style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Donor
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover">
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Type</th>
              <th>Total Donated</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {donors.map(donor => (
              <tr key={donor.id}>
                <td>
                  <button
                    className="btn btn-link p-0"
                    onClick={() => setSelectedDonor(donor.id)}
                  >
                    {donor.name}
                  </button>
                </td>
                <td>{donor.email}</td>
                <td>{donor.phone}</td>
                <td>{donor.donor_type}</td>
                <td>${parseFloat(donor.total_donated || 0).toFixed(2)}</td>
                <td>
                  <span className={`badge ${donor.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                    {donor.status}
                  </span>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-primary me-1"
                    onClick={() => {
                      setFormData(donor);
                      setModalType('donor');
                      setShowModal(true);
                    }}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteDonor(donor.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Donor Profile View
  const renderDonorProfile = () => {
    if (!donorProfile) return null;

    const { donor, donations, notes } = donorProfile;

    return (
      <div>
        <button
          className="btn btn-link mb-3 p-0"
          onClick={() => setSelectedDonor(null)}
        >
          <i className="bi bi-arrow-left me-2"></i>Back to Donors
        </button>

        <div className="row">
          <div className="col-md-4">
            <div className="card">
              <div className="card-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="mb-0">Donor Information</h5>
              </div>
              <div className="card-body">
                <h4>{donor.name}</h4>
                <p className="mb-1"><strong>Email:</strong> {donor.email}</p>
                <p className="mb-1"><strong>Phone:</strong> {donor.phone}</p>
                <p className="mb-1"><strong>Type:</strong> {donor.donor_type}</p>
                <p className="mb-1"><strong>Status:</strong>
                  <span className={`badge ms-2 ${donor.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                    {donor.status}
                  </span>
                </p>
                <hr />
                <h5>Statistics</h5>
                <p className="mb-1"><strong>Total Donated:</strong> ${parseFloat(donor.total_donated || 0).toFixed(2)}</p>
                <p className="mb-1"><strong>Donations:</strong> {donor.donation_count}</p>
                <p className="mb-1"><strong>First Donation:</strong> {donor.first_donation_date || 'N/A'}</p>
                <p className="mb-1"><strong>Last Donation:</strong> {donor.last_donation_date || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className="card mb-3">
              <div className="card-header" style={{ backgroundColor: '#2C5234', color: 'white' }}>
                <h5 className="mb-0">Donation History</h5>
              </div>
              <div className="card-body">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Campaign</th>
                      <th>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations?.map(donation => (
                      <tr key={donation.id}>
                        <td>{donation.donation_date}</td>
                        <td>${parseFloat(donation.amount).toFixed(2)}</td>
                        <td>{donation.campaign_title || 'General'}</td>
                        <td>{donation.payment_method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="mb-0">Communication Log</h5>
              </div>
              <div className="card-body">
                {notes?.map(note => (
                  <div key={note.id} className="border-bottom pb-2 mb-2 d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <small className="text-muted">{new Date(note.created_at).toLocaleDateString()}</small>
                      <p className="mb-0">{note.note}</p>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteNote(note.id)}
                      title="Delete note"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                ))}

                <div className="mt-3">
                  <textarea
                    className="form-control"
                    placeholder="Add a note..."
                    id="newNote"
                    rows="3"
                  ></textarea>
                  <div className="form-check mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sendEmailCheck"
                    />
                    <label className="form-check-label" htmlFor="sendEmailCheck">
                      <i className="bi bi-envelope me-1"></i>
                      Send email to donor from donate@africaresp.org
                    </label>
                  </div>
                  <button
                    className="btn btn-sm btn-success mt-2"
                    style={{ backgroundColor: '#6B8E23' }}
                    onClick={() => {
                      const note = document.getElementById('newNote').value;
                      const sendEmail = document.getElementById('sendEmailCheck').checked;
                      if (note) {
                        handleAddNote(note, sendEmail);
                        document.getElementById('newNote').value = '';
                        document.getElementById('sendEmailCheck').checked = false;
                      }
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Donations List View
  const renderDonations = () => (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h4>Donations Management</h4>
        <div>
          <button
            className="btn btn-outline-success me-2"
            onClick={exportDonationsCSV}
          >
            <i className="bi bi-download me-2"></i>Export CSV
          </button>
          <button
            className="btn btn-success"
            style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
            onClick={() => {
              setFormData({});
              setModalType('donation');
              setShowModal(true);
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>Record Donation
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover">
          <thead style={{ backgroundColor: '#f8f9fa' }}>
            <tr>
              <th>Date</th>
              <th>Donor</th>
              <th>Amount</th>
              <th>Campaign</th>
              <th>Payment Method</th>
              <th>Recurring</th>
            </tr>
          </thead>
          <tbody>
            {donations.map(donation => (
              <tr key={donation.id}>
                <td>{donation.donation_date}</td>
                <td>{donation.donor_name}</td>
                <td>${parseFloat(donation.amount).toFixed(2)}</td>
                <td>{donation.campaign_title || 'General'}</td>
                <td>{donation.payment_method}</td>
                <td>
                  {donation.is_recurring ?
                    <span className="badge bg-info">Recurring</span> :
                    <span className="badge bg-secondary">One-time</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Campaigns View
  const renderCampaigns = () => (
    <div>
      <div className="d-flex justify-content-between mb-3">
        <h4>Campaign Management</h4>
        <button
          className="btn btn-success"
          style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
          onClick={() => {
            setFormData({});
            setModalType('campaign');
            setShowModal(true);
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>Create Campaign
        </button>
      </div>

      <div className="row">
        {campaigns.map(campaign => (
          <div key={campaign.id} className="col-md-6 mb-3">
            <div className="card">
              <div className="card-body">
                <h5>{campaign.title}</h5>
                <p className="text-muted">{campaign.description}</p>
                <div className="mb-2">
                  <small>Goal: ${parseFloat(campaign.goal_amount).toFixed(2)}</small><br />
                  <small>Raised: ${parseFloat(campaign.raised_amount).toFixed(2)}</small><br />
                  <small>Donors: {campaign.donor_count}</small>
                </div>
                <div className="progress mb-2" style={{ height: '20px' }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${campaign.progress}%`, backgroundColor: '#6B8E23' }}
                  >
                    {campaign.progress}%
                  </div>
                </div>
                <span className={`badge ${campaign.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                  {campaign.status}
                </span>
                <button
                  className="btn btn-sm btn-outline-primary ms-2"
                  onClick={() => {
                    setFormData(campaign);
                    setModalType('campaign');
                    setShowModal(true);
                  }}
                >
                  <i className="bi bi-pencil"></i> Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Modal
  const renderModal = () => {
    if (!showModal) return null;

    const renderModalContent = () => {
      if (modalType === 'donor') {
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                type="text"
                className="form-control"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Address</label>
              <textarea
                className="form-control"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              ></textarea>
            </div>
            <div className="mb-3">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={formData.donor_type || 'Individual'}
                onChange={(e) => setFormData({ ...formData, donor_type: e.target.value })}
              >
                <option value="Individual">Individual</option>
                <option value="Corporate">Corporate</option>
                <option value="Foundation">Foundation</option>
                <option value="Organization">Organization</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status || 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        );
      } else if (modalType === 'donation') {
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Donor *</label>
              <select
                className="form-select"
                value={formData.donor_id || ''}
                onChange={(e) => setFormData({ ...formData, donor_id: e.target.value })}
              >
                <option value="">Select Donor</option>
                {donors.map(donor => (
                  <option key={donor.id} value={donor.id}>{donor.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Amount *</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Date *</label>
              <input
                type="date"
                className="form-control"
                value={formData.donation_date || new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData({ ...formData, donation_date: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Campaign</label>
              <select
                className="form-select"
                value={formData.campaign_id || ''}
                onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
              >
                <option value="">General Fund</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>{campaign.title}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Payment Method</label>
              <select
                className="form-select"
                value={formData.payment_method || 'Credit Card'}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              >
                <option value="Credit Card">Credit Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="PayPal">PayPal</option>
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
              </select>
            </div>
          </div>
        );
      } else if (modalType === 'campaign') {
        return (
          <div>
            <div className="mb-3">
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              ></textarea>
            </div>
            <div className="mb-3">
              <label className="form-label">Goal Amount *</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.goal_amount || ''}
                onChange={(e) => setFormData({ ...formData, goal_amount: e.target.value })}
              />
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.start_date || ''}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.end_date || ''}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.status || 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        );
      }
    };

    return (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
              <h5 className="modal-title">
                {modalType === 'donor' && (formData.id ? 'Edit Donor' : 'Add Donor')}
                {modalType === 'donation' && 'Record Donation'}
                {modalType === 'campaign' && (formData.id ? 'Edit Campaign' : 'Create Campaign')}
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => {
                  setShowModal(false);
                  setFormData({});
                }}
              ></button>
            </div>
            <div className="modal-body">
              {renderModalContent()}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  setFormData({});
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                onClick={() => {
                  if (modalType === 'donor') handleSaveDonor();
                  if (modalType === 'donation') handleSaveDonation();
                  if (modalType === 'campaign') handleSaveCampaign();
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'bi-speedometer2' },
    { id: 'donors', label: 'Donors', icon: 'bi-people' },
    { id: 'donations', label: 'Donations', icon: 'bi-cash-coin' },
    { id: 'campaigns', label: 'Campaigns', icon: 'bi-megaphone' }
  ];

  const renderContent = () => {
    if (selectedDonor && activeTab === 'donors') {
      return renderDonorProfile();
    }

    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'donors':
        return renderDonorsList();
      case 'donations':
        return renderDonations();
      case 'campaigns':
        return renderCampaigns();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="donor-management-container">
      {/* Header Section */}
      <div className="donor-header">
        <div className="d-flex align-items-center gap-3">
          <div className="header-icon">
            <i className="bi bi-heart-fill"></i>
          </div>
          <div>
            <h2 className="mb-0 fw-bold">Donor Management System</h2>
            <p className="text-muted mb-0 small">Track donations, manage donors, and monitor campaigns</p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="donor-nav-wrapper">
        <div className="d-flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`donor-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.id);
                setSelectedDonor(null);
              }}
            >
              <i className={`bi ${tab.icon}`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="donor-content">
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#6B8E23', width: '3rem', height: '3rem' }}></div>
            <p className="mt-3 text-muted">Loading...</p>
          </div>
        )}

        {!loading && renderContent()}
        {renderModal()}
      </div>

      {/* Add inline styles */}
      <style jsx>{`
        .donor-management-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 2rem;
        }

        .donor-header {
          background: linear-gradient(135deg, #6B8E23 0%, #58761C 100%);
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 8px 24px rgba(107, 142, 35, 0.2);
          margin-bottom: 2rem;
          animation: slideDown 0.5s ease-out;
        }

        .donor-header h2,
        .donor-header p {
          color: white;
        }

        .header-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          backdrop-filter: blur(10px);
        }

        .donor-nav-wrapper {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          margin-bottom: 2rem;
          animation: fadeIn 0.6s ease-out;
        }

        .donor-nav-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 1.5rem;
          background: white;
          color: #6B8E23;
          border: 2px solid #6B8E23;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
        }

        .donor-nav-btn i {
          font-size: 1.25rem;
        }

        .donor-nav-btn:hover {
          background: #f8f9fa;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(107, 142, 35, 0.2);
        }

        .donor-nav-btn.active {
          background: #6B8E23;
          color: white;
          box-shadow: 0 6px 20px rgba(107, 142, 35, 0.3);
          transform: translateY(-2px);
        }

        .donor-nav-btn.active:hover {
          background: #58761C;
        }

        .donor-content {
          animation: fadeIn 0.7s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @media (max-width: 768px) {
          .donor-management-container {
            padding: 1rem;
          }

          .donor-header {
            padding: 1.5rem;
          }

          .header-icon {
            width: 50px;
            height: 50px;
            font-size: 24px;
          }

          .donor-nav-btn {
            padding: 0.75rem 1.25rem;
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DonorManagementSystem;
